import type { FC } from 'react';
import type { ArticleHead } from './types';
import { formatRelativeDate } from '../../utils/date-format';
import { Badge } from '../../components/ui/badge';
import { IconButton } from '../../components/ui/icon-button';
import { useTranslation } from '../../i18n/I18nContext';

interface ArticleCardProps {
  article: ArticleHead;
  onClick: () => void;
  onDelete: (e: React.MouseEvent) => void;
}

const OPENED_KEY = 'polyreader_opened_articles';

function getOpenedArticles(): Set<string> {
  try {
    return new Set(JSON.parse(localStorage.getItem(OPENED_KEY) || '[]'));
  } catch {
    return new Set();
  }
}

export function markArticleOpened(articleId: string): void {
  const opened = getOpenedArticles();
  opened.add(articleId);
  localStorage.setItem(OPENED_KEY, JSON.stringify([...opened]));
}

export function removeOpenedArticle(articleId: string): void {
  const opened = getOpenedArticles();
  opened.delete(articleId);
  localStorage.setItem(OPENED_KEY, JSON.stringify([...opened]));
}

function getStatusBadge(article: ArticleHead, t: (key: string, params?: Record<string, string | number>) => string): { label: string; variant: 'warning' | 'info' | 'success' } | null {
  if (article.processingStatus === 'error') return null;
  if (article.processingStatus === 'ready') {
    if (getOpenedArticles().has(article.articleId)) return null;
    return { label: t('article_card.ready'), variant: 'info' };
  }

  // Show fine-grained progress when pipeline tasks are available
  if (article.pipelineTasks) {
    const statuses = Object.values(article.pipelineTasks);
    const total = statuses.length;
    const done = statuses.filter(s => s === 'completed' || s === 'error').length;
    const pct = total > 0 ? Math.round((done / total) * 100) : 0;
    return { label: t('article_card.tasks_progress', { pct }), variant: 'warning' };
  }

  return { label: t('article_card.processing'), variant: 'warning' };
}

const ArticleCard: FC<ArticleCardProps> = ({ article, onClick, onDelete }) => {
  const { t } = useTranslation();
  const dateStr = article.timestamp || article.createdAt;
  const isReady = article.processingStatus === 'ready';
  const hasImage = !!article.img;
  const statusBadge = getStatusBadge(article, t);

  return (
    <div
      onClick={isReady ? onClick : undefined}
      className={`w-full text-left border border-border rounded-lg bg-card overflow-hidden transition-colors select-auto ${
        isReady ? 'cursor-pointer hover:bg-muted/30' : 'cursor-default'
      }`}
    >
      <div className="flex">
        {hasImage && (
          <div className="hidden sm:block shrink-0 w-24 h-auto bg-muted">
            <img
              src={`${import.meta.env.VITE_API_URL}/reader/articles/images/${article.img}`}
              alt=""
              className="w-full h-full object-cover"
              onError={(e) => {
                (e.target as HTMLImageElement).parentElement!.style.display = 'none';
              }}
            />
          </div>
        )}

        <div className="flex-1 p-2.5 sm:p-3">
          {hasImage && (
            <div className="sm:hidden mb-2 -mx-2.5 -mt-2.5 sm:-mx-3 sm:-mt-3 bg-muted">
              <img
                src={`${import.meta.env.VITE_API_URL}/reader/articles/images/${article.img}`}
                alt=""
                className="w-full h-28 object-cover"
                onError={(e) => {
                  (e.target as HTMLImageElement).parentElement!.style.display = 'none';
                }}
              />
            </div>
          )}

          <div className="flex items-start justify-between gap-1.5">
            <div className="min-w-0 flex-1">
              <div className="flex items-center justify-between gap-1.5 mb-1 flex-wrap">
                <div className="flex items-center gap-1.5 flex-wrap">
                  {article.lang && (
                    <Badge variant="indigo" size="sm" title={t('badge.language')}>
                      {article.lang.toUpperCase()}
                    </Badge>
                  )}
                  {article.cefrLevel && (
                    <Badge variant="pink" size="sm" title={t('badge.cefr')}>
                      {article.cefrLevel}
                    </Badge>
                  )}
                  {article.wordCount != null && article.wordCount > 0 ? (
                    <Badge variant="secondary" size="sm" title={t('badge.word_count')}>
                      {t('article_card.words', { count: article.wordCount })}
                    </Badge>
                  ) : article.targetWordCount != null && article.targetWordCount > 0 ? (
                    <Badge variant="secondary" size="sm" title={t('badge.word_count')}>
                      ~{t('article_card.words', { count: article.targetWordCount })}
                    </Badge>
                  ) : null}
                  {dateStr && (
                    <Badge variant="secondary" size="sm" title={t('badge.date')}>
                      {formatRelativeDate(dateStr, t)}
                    </Badge>
                  )}
                </div>
                {article.processingStatus === 'error' ? (
                  <Badge variant="destructive" size="sm" title={article.errorMessage || t('badge.status_error')}>{t('article_card.error')}</Badge>
                ) : statusBadge ? (
                  <Badge variant={statusBadge.variant} size="sm" title={t('badge.status_processing')}>{statusBadge.label}</Badge>
                ) : null}
              </div>

              <h3 className={`text-base sm:text-lg line-clamp-1 ${article.title ? 'font-semibold text-foreground' : 'italic text-muted-foreground'}`}>
                {article.title || article.displayHint || ''}
              </h3>

              {article.description && (
                <p className="text-sm text-muted-foreground mt-0.5 line-clamp-2">
                  {article.description}
                </p>
              )}
            </div>

            <div className="shrink-0 flex items-start">
              <IconButton
                onClick={onDelete}
                size="card"
                title={t('article_card.delete_title')}
                tooltipPosition="below"
                tooltipAlign="end"
                aria-label={t('common.delete_article')}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M3 6h18" /><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" /><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
                </svg>
              </IconButton>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ArticleCard;
